// Renders prominent markers for comuni passed via `?focus=ISTAT1,ISTAT2,...`
// on the map URL. These are the top-10 comuni a user expanded inside a
// RegionCard on /it/recommendations and clicked "View on map". Without
// this layer the user lands on the map with no visual hint about which
// comuni were recommended — they're lost in 8 000 dots.
//
// Visual contract:
//   - Distinctive terra-600 ring with white inner dot (larger than ordinary
//     comune markers) + always-on label so they read at any zoom.
//   - The label is rendered as an HTML divIcon (not a Leaflet Tooltip) —
//     `permanent` Tooltips were getting dropped on re-renders triggered by
//     the choropleth view switch at zoom 10, leaving bare dots behind.
//     A divIcon-Marker is just a DOM node Leaflet repositions; it survives
//     every zoom and store update.
//   - When the user clicks one of the recommended comuni and DetailsPanel
//     opens for it, that single label is hidden so the user gets a clean
//     view of the surrounding context. All other labels stay.
//   - Non-focused comuni are dimmed to 50% via a CSS variable that the
//     ChoroplethLayer style reads (see `useFocusedIstats`).
//
// Listens to `useSearchParams` instead of the hash blob so a sharable
// URL like `/it/map?focus=…&region=…` survives the useUrlSync rewrites.
import { useMemo } from 'react';
import { CircleMarker, Marker } from 'react-leaflet';
import L from 'leaflet';
import { useQuery } from '@tanstack/react-query';
import { queries } from '@/data';
import { useDetailsStore } from '@/features/details';
import { useFocusedIstats } from './useFocusedIstats';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

function makeLabelIcon(rank: number, name: string): L.DivIcon {
  const safe = escapeHtml(`#${rank} ${name}`);
  return L.divIcon({
    html: `<span class="focus-label-pill">${safe}</span>`,
    className: 'focus-label-icon',
    iconSize: [1, 1],
    iconAnchor: [0, 14], // anchor sits at the dot; pill is offset upward via CSS
  });
}

export function FocusComuniLayer() {
  const focused = useFocusedIstats();
  const openIstat = useDetailsStore((s) => s.openIstat);
  const { data: meta } = useQuery(queries.meta());
  const version = meta?.version ?? '';
  const { data: lightIndex } = useQuery({
    ...queries.lightIndex(version),
    enabled: !!version && focused.size > 0,
  });

  const points = useMemo(() => {
    if (!lightIndex || focused.size === 0) return [];
    const out: { istat: string; name: string; lat: number; lon: number }[] = [];
    for (const istat of focused) {
      const c = lightIndex.byIstat.get(istat);
      if (!c) continue;
      out.push({ istat, name: c.name, lat: c.lat, lon: c.lon });
    }
    return out;
  }, [lightIndex, focused]);

  if (points.length === 0) return null;

  return (
    <>
      {points.map((p) => (
        <CircleMarker
          key={`dot-${p.istat}`}
          center={[p.lat, p.lon]}
          pane="selectionPane"
          radius={9}
          interactive={false}
          pathOptions={{
            color: '#b34a2a' /* terra-600 */,
            weight: 3,
            opacity: 1,
            fillColor: '#fff7ed' /* terra-50 — bright inner */,
            fillOpacity: 0.95,
          }}
        />
      ))}
      {points.map((p, i) => {
        // Hide only the label of the comune the user has actively opened —
        // that one already has the DetailsPanel + LOCKED choropleth highlight,
        // so a duplicate pill on top would crowd it. Every other recommended
        // comune keeps its label so the user retains the "10 recommended"
        // mental model while exploring.
        if (openIstat === p.istat) return null;
        return (
          <Marker
            key={`label-${p.istat}`}
            position={[p.lat, p.lon]}
            pane="selectionPane"
            icon={makeLabelIcon(i + 1, p.name)}
            interactive={false}
            zIndexOffset={1000}
          />
        );
      })}
    </>
  );
}
